"use client";

import React from "react";
import { Calendar, Clock, User, ArrowLeft, Share2, PhoneCall, CircleCheck as CheckCircle2, Bookmark } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare global {
  interface Window {
    wvcClient?: {
      getCurrentWPQueryParams: () => Record<string, any>;
    };
  }
}

function SinglePostContent() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-8 animate-pulse py-12">
        <div className="h-6 w-32 rounded-md bg-muted" />
        <div className="h-12 w-3/4 rounded-lg bg-muted" />
        <div className="flex items-center gap-4">
          <div className="size-12 rounded-full bg-muted" />
          <div className="space-y-2">
            <div className="h-4 w-32 rounded bg-muted" />
            <div className="h-3 w-24 rounded bg-muted" />
          </div>
        </div>
        <div className="h-96 w-full rounded-2xl bg-muted" />
        <div className="space-y-4">
          <div className="h-4 w-full rounded bg-muted" />
          <div className="h-4 w-full rounded bg-muted" />
          <div className="h-4 w-5/6 rounded bg-muted" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-xl py-16 text-center">
        <p className="text-destructive font-medium">Unable to load article</p>
        <p className="mt-2 text-sm text-muted-foreground">
          There was an issue fetching the requested post content.
        </p>
        <Button onClick={() => refetch()} variant="outline" className="mt-6">
          Try Again
        </Button>
      </div>
    );
  }

  const post = posts?.[0];

  if (!post) {
    return (
      <div className="mx-auto max-w-xl py-16 text-center">
        <h2 className="text-2xl font-bold font-default text-foreground">
          Article Not Found
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The plumbing guide or post you are looking for is currently unavailable.
        </p>
        <div className="mt-6">
          <Link to="/">
            <Button variant="default">Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const authorName = post.author?.name || "Jonesboro Emergency Plumber";
  const authorAvatar = post.author?.avatar?.["96"] || post.author?.avatar?.["48"] || "";
  const postDate = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric"
      })
    : "Recently Updated";

  return (
    <article className="mx-auto max-w-4xl">
      {/* Top Navigation / Breadcrumb */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="size-4" />
          Back to Overview
        </Link>
        <div className="flex items-center gap-2">
          {post.categories && post.categories.length > 0 && (
            <span className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground">
              {post.categories[0].name}
            </span>
          )}
        </div>
      </div>

      {/* Post Header */}
      <header className="space-y-6">
        <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl font-default">
          {post.title}
        </h1>

        {/* Metadata bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-border bg-card p-4 text-card-foreground">
          <div className="flex items-center gap-3">
            <Avatar className="size-12 border border-border">
              <AvatarImage src={authorAvatar} alt={authorName} />
              <AvatarFallback className="bg-primary text-primary-foreground">
                {authorName.substring(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                {authorName}
                <CheckCircle2 className="size-3.5 text-accent" />
              </p>
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Calendar className="size-3.5" />
                  {postDate}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock className="size-3.5" />
                  4 min read
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <a href="tel:(470)847-9076">
              <Button size="sm" variant="default" className="gap-1.5 font-semibold">
                <PhoneCall className="size-4" />
                (470) 847-9076
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Featured Image */}
      {post.featuredImage && (
        <div className="my-8 overflow-hidden rounded-2xl border border-border shadow-sm">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="aspect-video w-full object-cover object-center"
          />
        </div>
      )}

      {/* Main Content */}
      <div className="mt-8 space-y-6 text-foreground leading-relaxed">
        {post.content ? (
          <div
            className="prose prose-slate max-w-none prose-headings:font-default prose-headings:font-bold prose-headings:text-foreground prose-p:text-foreground prose-a:text-primary prose-a:underline hover:prose-a:text-primary/80 prose-strong:text-foreground prose-li:text-foreground"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        ) : post.excerpt ? (
          <div
            className="prose prose-slate max-w-none text-base"
            dangerouslySetInnerHTML={{ __html: post.excerpt }}
          />
        ) : (
          <div className="space-y-4">
            <p className="text-lg leading-relaxed text-foreground">
              Plumbing emergencies require swift, calculated steps to prevent severe property damage and excessive repair costs. Whether dealing with a sudden pipe rupture, severe drain blockage, or complete water heater malfunction in Jonesboro, GA, prompt response and proper isolation are key.
            </p>
            <p className="leading-relaxed text-foreground">
              Our certified technicians are on standby 24 hours a day, 7 days a week. With upfront diagnostics and immediate dispatch, we ensure local homeowners and businesses receive dependable service without delay.
            </p>
          </div>
        )}
      </div>

      <Separator className="my-12" />

      {/* Author Bio Box */}
      <div className="rounded-2xl border border-border bg-card p-6 text-card-foreground shadow-xs sm:p-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <Avatar className="size-16 shrink-0 border-2 border-border">
            <AvatarImage src={authorAvatar} alt={authorName} />
            <AvatarFallback className="bg-primary text-primary-foreground text-lg">
              {authorName.substring(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="space-y-2 flex-1">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-foreground font-default">
                Written by {authorName}
              </h3>
            </div>
            <p className="text-sm text-muted-foreground">
              Local master plumbers dedicated to 24/7 fast-response emergency plumbing, sewer line inspections, leak detection, and complete system restoration throughout Jonesboro and surrounding Clayton County communities.
            </p>
          </div>
        </div>
      </div>

      {/* Urgent Callout Box */}
      <Card className="mt-10 border-accent/40 bg-secondary/50">
        <CardContent className="flex flex-col sm:flex-row items-center justify-between gap-6 p-6 sm:p-8">
          <div className="space-y-1 text-center sm:text-left">
            <h4 className="text-xl font-bold text-foreground font-default">
              Facing an active plumbing emergency?
            </h4>
            <p className="text-sm text-muted-foreground">
              Our local emergency truck is ready to roll out immediately.
            </p>
          </div>
          <a href="tel:(470)847-9076" className="w-full sm:w-auto">
            <Button size="lg" className="w-full sm:w-auto font-bold bg-primary text-primary-foreground hover:bg-primary/90">
              <PhoneCall className="size-5 mr-2" />
              Call (470) 847-9076
            </Button>
          </a>
        </CardContent>
      </Card>

      {/* Recommended Articles Section */}
      <div className="mt-16 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-2xl font-bold font-default text-foreground">
            Recommended Guides & Resources
          </h3>
          <Link to="/problems/" className="text-sm font-semibold text-primary hover:underline">
            View All Services
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="hover:border-primary/50 transition-[border-color,box-shadow]">
            <CardContent className="p-6 space-y-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent-foreground bg-accent/20 px-2.5 py-1 rounded-full">
                <Bookmark className="size-3 text-accent" />
                Emergency Advice
              </span>
              <h4 className="text-lg font-semibold font-default text-foreground">
                How to Locate and Shut Off Your Main Water Valve in 60 Seconds
              </h4>
              <p className="text-sm text-muted-foreground line-clamp-2">
                Essential step-by-step instructions for Jonesboro residents when a major pipe bursts inside the home.
              </p>
              <div className="pt-2">
                <Link to="/services/">
                  <Button variant="outline" size="sm">
                    Read Details
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          <Card className="hover:border-primary/50 transition-[border-color,box-shadow]">
            <CardContent className="p-6 space-y-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent-foreground bg-accent/20 px-2.5 py-1 rounded-full">
                <Bookmark className="size-3 text-accent" />
                Maintenance
              </span>
              <h4 className="text-lg font-semibold font-default text-foreground">
                Warning Signs Your Water Heater is About to Fail
              </h4>
              <p className="text-sm text-muted-foreground line-clamp-2">
                Learn how to detect rumbling sounds, discolored water, and slow heating before a disastrous flood occurs.
              </p>
              <div className="pt-2">
                <Link to="/water-heater/">
                  <Button variant="outline" size="sm">
                    Read Details
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </article>
  );
}

export default function Article() {
  const currentWPQueryParams =
    typeof window !== "undefined" && window.wvcClient?.getCurrentWPQueryParams
      ? window.wvcClient.getCurrentWPQueryParams()
      : { post_type: "post", per_page: 1 };

  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section
      id="single-post-template-section"
      data-nav="light"
      className="bg-background text-foreground py-12 md:py-20 px-4 sm:px-6 lg:px-8 border-b border-border"
    >
      <div className="container mx-auto max-w-5xl">
        <PostsProvider wp_query={wp_query} className="w-full">
          <SinglePostContent />
        </PostsProvider>
      </div>
    </section>
  );
}